import { useEffect } from "react";

const APP_URL = "https://aevumpilot.app/";
const LOGIN_URL = "https://aevumpilot.app/login";

export default function LandingPage() {
  useEffect(() => {
    document.title = "Aevum — Your whole admin life, on one screen.";
    const set = (name, val) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", val);
    };
    set("description", "Aevum is the personal operating system for people running a life and a business at once. It tells you what needs you today, tracks every renewal and bill, and keeps your money in one place.");
    ["og:title", "twitter:title"].forEach((k) => set(k, "Aevum — Your whole admin life, on one screen."));
    ["og:description", "twitter:description"].forEach((k) => set(k, "The proactive admin OS. Renewals, bills, capture, money — all on one screen."));
    set("theme-color", "#6750A4");
    return () => { document.title = "Aevum"; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBFE] text-[#1D1B20] antialiased" data-testid="landing-page">
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
        .reveal { animation: slideUp .8s cubic-bezier(.2,0,0,1) both; }
        .reveal-1 { animation-delay: 80ms; } .reveal-2 { animation-delay: 180ms; } .reveal-3 { animation-delay: 280ms; }
        @media (prefers-reduced-motion) { .reveal { animation: none !important; } }
        .grain::after {
          content:""; position:absolute; inset:0; opacity:.03; pointer-events:none;
          background: repeating-radial-gradient(circle at 20% 10%, rgba(29,27,32,.5), transparent 2px);
        }
      `}</style>
      <Nav />
      <Hero />
      <Problem />
      <Today />
      <Renewals />
      <Capture />
      <Money />
      <HowItWorks />
      <FeatureGrid />
      <Security />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#FFFBFE]/85 border-b border-[#E7E0EC]/60" data-testid="landing-nav">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-6">
        <a href="#hero" className="flex items-center gap-2 text-lg font-medium tracking-tight text-[#1D1B20]">
          <span className="w-8 h-8 rounded-xl bg-[#6750A4] text-white flex items-center justify-center font-bold">Æ</span>
          Aevum
        </a>
        <div className="hidden md:flex items-center gap-6 text-sm text-[#49454F]">
          <a href="#today" className="hover:text-[#1D1B20] transition-colors">Features</a>
          <a href="#how" className="hover:text-[#1D1B20] transition-colors">How it works</a>
          <a href="#money" className="hover:text-[#1D1B20] transition-colors">Money</a>
          <a href="#security" className="hover:text-[#1D1B20] transition-colors">Security</a>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <a href={LOGIN_URL} className="text-sm text-[#49454F] hover:text-[#1D1B20] transition-colors" data-testid="nav-login">Log in</a>
          <a href={APP_URL} className="inline-flex items-center gap-2 rounded-full bg-[#6750A4] text-white text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[.98] transition-[transform,filter] duration-200" data-testid="nav-cta">
            Get started
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section id="hero" className="relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 pt-20 pb-28 grid lg:grid-cols-[1.1fr,1fr] gap-16 items-center">
        <div>
          <span className="reveal inline-flex items-center gap-2 rounded-full bg-[#EADDFF] text-[#4F378B] text-[11px] font-medium tracking-widest uppercase px-3 py-1">Personal admin OS</span>
          <h1 className="reveal reveal-1 mt-6 text-5xl sm:text-6xl lg:text-7xl font-light tracking-[-0.02em] leading-[1.02] text-[#1D1B20]">
            Your whole admin life,<br /><span className="text-[#6750A4]">on one screen.</span>
          </h1>
          <p className="reveal reveal-2 mt-6 text-lg text-[#49454F] max-w-lg leading-relaxed">
            Aevum is the personal operating system for people running a life and a business at once — it tells you what needs you today, tracks every renewal and bill, and keeps your money in one place.
          </p>
          <div className="reveal reveal-3 mt-8 flex items-center gap-4">
            <a href={APP_URL} className="inline-flex items-center gap-2 rounded-full bg-[#6750A4] text-white text-base font-medium px-6 py-3 hover:brightness-110 active:scale-[.98] shadow-[0_8px_24px_rgba(103,80,164,.3)] transition-[transform,filter] duration-200" data-testid="hero-cta">
              Get started →
            </a>
            <a href={LOGIN_URL} className="text-base text-[#49454F] hover:text-[#1D1B20] underline-offset-4 hover:underline transition-colors" data-testid="hero-login">Log in</a>
          </div>
        </div>
        <MockToday />
      </div>
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-[#EADDFF]/40 blur-3xl -z-10 translate-x-1/3 -translate-y-1/3" />
    </section>
  );
}

function MockToday() {
  return (
    <div className="relative reveal reveal-2">
      <div className="absolute -inset-8 bg-gradient-to-br from-[#6750A4]/10 to-transparent rounded-3xl blur-2xl" />
      <div className="relative bg-white rounded-[28px] shadow-2xl border border-[#E7E0EC] p-6 overflow-hidden">
        <div className="flex items-center gap-2 text-xs text-[#49454F]">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-yellow-400" />
          <span className="w-2 h-2 rounded-full bg-green-400" />
          <span className="ml-2 tracking-widest uppercase text-[10px]">Today · Friday</span>
        </div>
        <h3 className="mt-5 text-2xl font-light tracking-tight">Good morning, Alex.</h3>
        <div className="mt-4 rounded-2xl bg-[#F7F2FA] p-4 text-sm text-[#49454F] leading-relaxed">
          <span className="font-medium text-[#6750A4]">The Brain:</span> Three things need you today. Your passport renewal window opens Monday, the Netflix subscription bills tomorrow (A$18.99), and Jamie's invoice is 4 days overdue.
        </div>
        <ul className="mt-5 space-y-2">
          {[
            { p: "high", t: "Reply to Jamie's invoice", d: "Overdue · 4d" },
            { p: "med",  t: "Rent · Frangipani", d: "In 8 days · A$2,500" },
            { p: "low",  t: "Book flu shot", d: "Sometime this month" },
          ].map((i) => (
            <li key={i.t} className="flex items-center gap-3 py-2 border-b border-[#E7E0EC]/60 last:border-0">
              <span className={`w-1.5 h-6 rounded-full ${i.p==="high"?"bg-red-500":i.p==="med"?"bg-[#6750A4]":"bg-[#CAC4D0]"}`} />
              <div className="flex-1"><div className="text-sm text-[#1D1B20]">{i.t}</div><div className="text-xs text-[#49454F]">{i.d}</div></div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Problem() {
  return (
    <section className="max-w-4xl mx-auto px-6 py-24 text-center">
      <p className="text-xl sm:text-2xl text-[#1D1B20] leading-relaxed">
        Tasks in one app. Bills in a spreadsheet. Renewals in your head. Follow-ups in your inbox.<br />
        <span className="text-[#6750A4]">The gaps are where things get missed.</span>
      </p>
    </section>
  );
}

function Section({ id, eyebrow, title, body, visual, reverse }) {
  return (
    <section id={id} className="py-24 border-t border-[#E7E0EC]/60">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div className={reverse ? "lg:order-2" : ""}>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#EADDFF] text-[#4F378B] text-[11px] font-medium tracking-widest uppercase px-3 py-1">{eyebrow}</span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-[-0.01em] leading-[1.1] text-[#1D1B20]">{title}</h2>
          <p className="mt-5 text-lg text-[#49454F] leading-relaxed max-w-lg">{body}</p>
        </div>
        <div className={reverse ? "lg:order-1" : ""}>{visual}</div>
      </div>
    </section>
  );
}

function Today() {
  const visual = (
    <div className="relative">
      <div className="bg-white rounded-[24px] shadow-xl border border-[#E7E0EC] p-6">
        <div className="text-[10px] uppercase tracking-widest text-[#49454F]">AI daily brief</div>
        <p className="mt-3 text-base text-[#1D1B20] leading-relaxed">"Your biggest risk today is the passport window closing on Monday. Handle it before the client meeting at 2pm — everything else can wait."</p>
        <div className="mt-4 flex items-center gap-2 text-xs text-[#6750A4]"><span className="w-1.5 h-1.5 rounded-full bg-[#6750A4] animate-pulse" /> Grounded in 27 items across your workspace</div>
      </div>
    </div>
  );
  return <Section id="today" eyebrow="The Today brief" title="It tells you what needs you today." body="Stop opening five apps to figure out what to do. Aevum's proactive home surfaces overdue items, renewals inside their lead time, and the one thing worth doing first — powered by an AI briefing that only sees your own data." visual={visual} />;
}

function Renewals() {
  const visual = (
    <div className="grid grid-cols-2 gap-3">
      {[
        { t: "Passport", d: "18 days", tone: "primary" },
        { t: "Domain: aevum.io", d: "3 days", tone: "danger" },
        { t: "Insurance", d: "62 days", tone: "muted" },
        { t: "Company return", d: "9 days", tone: "primary" },
      ].map((c) => (
        <div key={c.t} className={`rounded-2xl p-4 ${c.tone === "danger" ? "bg-red-50 border border-red-200" : c.tone === "primary" ? "bg-[#EADDFF]" : "bg-[#F7F2FA]"}`}>
          <div className="text-[10px] uppercase tracking-widest text-[#49454F]">Renews in</div>
          <div className={`text-2xl font-light mt-1 ${c.tone === "danger" ? "text-red-600" : "text-[#1D1B20]"}`}>{c.d}</div>
          <div className="mt-2 text-sm text-[#1D1B20]">{c.t}</div>
        </div>
      ))}
    </div>
  );
  return <Section id="renewals" eyebrow="Renewals & obligations" title="Every renewal, with a countdown." body="Passports, ABN returns, insurance, domains, quarterly BAS. Aevum treats renewals and obligations as first-class objects with lead-time countdowns, so nothing lapses because it was buried in an inbox." visual={visual} reverse />;
}

function Capture() {
  const visual = (
    <div className="bg-white rounded-[24px] shadow-xl border border-[#E7E0EC] p-6">
      <div className="text-[10px] uppercase tracking-widest text-[#49454F] mb-3">Quick capture</div>
      <div className="rounded-2xl border-2 border-[#6750A4] px-4 py-3 text-base text-[#1D1B20]">Renew my TFN by end of Feb, high priority</div>
      <div className="mt-4 flex items-center gap-2 text-xs text-[#49454F]"><span className="animate-pulse">✨</span> Reading…</div>
      <div className="mt-4 rounded-2xl bg-[#F7F2FA] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#4F378B]">Filed as</div>
        <div className="text-sm mt-1">
          <div><span className="text-[#49454F]">Type: </span>Renewal</div>
          <div><span className="text-[#49454F]">Space: </span>Life admin</div>
          <div><span className="text-[#49454F]">Due: </span>28 Feb 2026</div>
          <div><span className="text-[#49454F]">Priority: </span>High</div>
        </div>
      </div>
    </div>
  );
  return <Section id="capture" eyebrow="Quick Capture" title="One box. Anything in, filed automatically." body="Type or paste anything — a bill, a follow-up, a half-baked thought — and Aevum reads it, classifies it, and drops it into the right shape. No forms. No fields. No fiddling." visual={visual} />;
}

function Money() {
  const visual = (
    <div className="bg-white rounded-[24px] shadow-xl border border-[#E7E0EC] p-6 space-y-3">
      <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-widest text-[#49454F]">Net position</div><div className="text-xs text-[#6750A4]">Live · SBD</div></div>
      <div className="text-4xl font-light text-[#1D1B20] tabular-nums">$56,412,504</div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <span className="rounded-full bg-[#EADDFF] text-[#4F378B] font-medium px-2 py-0.5">6/9 budgets on track</span>
        <span className="rounded-full bg-red-100 text-red-700 font-medium px-2 py-0.5">2 bills due in 3d</span>
      </div>
      <div className="pt-3 border-t border-[#E7E0EC] grid grid-cols-3 gap-3 text-xs">
        {[["Rent","-A$2,500","4d"],["Netflix","-A$19","tomorrow"],["Salary","+A$8,400","5d"]].map((r) => (
          <div key={r[0]} className="rounded-xl bg-[#F7F2FA] p-3"><div className="text-[10px] uppercase tracking-widest text-[#49454F]">{r[2]}</div><div className="mt-1 font-medium text-[#1D1B20]">{r[0]}</div><div className="text-sm tabular-nums">{r[1]}</div></div>
        ))}
      </div>
    </div>
  );
  return <Section id="money" eyebrow="Money" title="A full budget & finance system, not a widget." body="Bank-feed automation, detected bills, reconciled to the cent. Recurring rules for weekly and monthly outflows. Goals that project the month you'll hit them. Every figure integer-accurate — no floating-point money." visual={visual} reverse />;
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Capture anything", b: "One box, free text. Paste an email, dictate a thought, forward a bill." },
    { n: "02", t: "Aevum organises it", b: "Type, space, priority, due date. Merchant, category, amount. All inferred." },
    { n: "03", t: "See only what needs you today", b: "The rest lives in Workspace, ready when you are." },
  ];
  return (
    <section id="how" className="py-24 border-t border-[#E7E0EC]/60 bg-[#F7F2FA]">
      <div className="max-w-6xl mx-auto px-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#4F378B] text-[11px] font-medium tracking-widest uppercase px-3 py-1">How it works</span>
        <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight max-w-2xl">Three steps between chaos and a calm morning.</h2>
        <ol className="mt-12 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <li key={s.n} className="bg-white rounded-3xl p-8 border border-[#E7E0EC]">
              <div className="text-6xl font-light text-[#6750A4] tabular-nums">{s.n}</div>
              <div className="mt-4 text-xl font-medium text-[#1D1B20]">{s.t}</div>
              <p className="mt-2 text-base text-[#49454F] leading-relaxed">{s.b}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FeatureGrid() {
  const features = [
    { t: "The Brain", b: "A private AI assistant that only ever sees your own data. Ask, plan, brief." },
    { t: "Four synchronised views", b: "Board, Grid, Timeline, Today — one record, seen through four lenses." },
    { t: "Email & calendar integrations", b: "Gmail and Google Calendar surface inbox items as triageable suggestions." },
    { t: "Triage inbox", b: "One inbound stream. Accept, reject, or edit before anything joins your Workspace." },
    { t: "Renewals & obligations", b: "First-class objects with lead-time countdowns and payment linking." },
    { t: "Multi-workspace", b: "Personal life and business admin, side by side, isolated. Invite teammates." },
  ];
  return (
    <section className="py-24 border-t border-[#E7E0EC]/60">
      <div className="max-w-6xl mx-auto px-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#EADDFF] text-[#4F378B] text-[11px] font-medium tracking-widest uppercase px-3 py-1">Everything else</span>
        <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight max-w-2xl">A quiet control tower, not a busy dashboard.</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.t} className="rounded-3xl border border-[#E7E0EC] p-6 hover:border-[#6750A4]/40 transition-colors">
              <div className="text-lg font-medium text-[#1D1B20]">{f.t}</div>
              <p className="mt-2 text-sm text-[#49454F] leading-relaxed">{f.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  return (
    <section id="security" className="py-24 bg-[#1D1B20] text-[#E7E0EC] border-t border-[#E7E0EC]/60">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 text-[#EADDFF] text-[11px] font-medium tracking-widest uppercase px-3 py-1">Trust</span>
        <h2 className="mt-6 text-4xl sm:text-5xl font-light tracking-tight">Your data helps only you.</h2>
        <p className="mt-6 text-lg text-[#CAC4D0] leading-relaxed max-w-2xl mx-auto">
          Per-user data isolation. The Brain only sees your own workspace, never anyone else's. Your bank feeds, your emails, your calendar — all locked inside your account.
        </p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section id="cta" className="py-32">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-5xl sm:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-[#1D1B20]">Calm mornings, on one screen.</h2>
        <p className="mt-6 text-lg text-[#49454F] max-w-xl mx-auto">Set up in under two minutes. Nothing to migrate — just start capturing.</p>
        <div className="mt-10 flex items-center justify-center gap-4">
          <a href={APP_URL} className="inline-flex items-center gap-2 rounded-full bg-[#6750A4] text-white text-lg font-medium px-8 py-4 hover:brightness-110 active:scale-[.98] shadow-[0_12px_36px_rgba(103,80,164,.35)] transition-[transform,filter] duration-200" data-testid="final-cta">
            Get started →
          </a>
          <a href={LOGIN_URL} className="text-base text-[#49454F] hover:text-[#1D1B20] underline-offset-4 hover:underline transition-colors" data-testid="final-login">Log in</a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#E7E0EC]/60 py-12">
      <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-[#49454F]">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#6750A4] text-white flex items-center justify-center text-xs font-bold">Æ</span>
          <span className="font-medium text-[#1D1B20]">Aevum</span>
          <span className="text-xs">© 2026</span>
        </div>
        <div className="flex items-center gap-5">
          <a href="#today" className="hover:text-[#1D1B20] transition-colors">Features</a>
          <a href="#how" className="hover:text-[#1D1B20] transition-colors">How it works</a>
          <a href={LOGIN_URL} className="hover:text-[#1D1B20] transition-colors">Log in</a>
          <a href={APP_URL} className="text-[#6750A4] font-medium hover:brightness-110 transition-all">Open Aevum →</a>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#79747E]">
          <a href="#" className="hover:text-[#1D1B20]">Privacy</a>
          <a href="#" className="hover:text-[#1D1B20]">Terms</a>
          <a href="#" className="hover:text-[#1D1B20]">Contact</a>
        </div>
      </div>
    </footer>
  );
}
